"use client";

import { useMemo } from "react";
import { cn } from "@/lib/format";
import type { PackageTier } from "@/lib/dto";

type Props = {
  tiers: Record<string, PackageTier>;
  selectedKey: string;
  onSelect: (key: string) => void;
};

/**
 * Tier selector for the Cost Details section — matches production's real
 * markup exactly (confirmed via the live DOM): a vertical rail of plain
 * buttons on the left of the panel (horizontal on small screens), each with
 * an arrow-pointer pseudo-element on the selected one. Production shows just
 * the tier name here (no price/description), so this is a simple label rail,
 * not the price-card tabs the previous build rendered.
 */
export function PackageTierCards({ tiers, selectedKey, onSelect }: Props) {
  const entries = useMemo(
    () =>
      Object.entries(tiers).sort(
        ([, a], [, b]) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
      ),
    [tiers],
  );

  if (entries.length === 0) return null;

  const moveSelection = (nextIndex: number) => {
    const normalizedIndex = (nextIndex + entries.length) % entries.length;
    const nextKey = entries[normalizedIndex]?.[0];
    if (!nextKey) return;
    onSelect(nextKey);
    window.requestAnimationFrame(() => {
      document.getElementById(`package-tier-tab-${nextKey}`)?.focus();
    });
  };

  return (
    <div
      className="relative mr-[-1px] mb-6 flex flex-wrap gap-1.5 lg:flex-col"
      role="tablist"
      aria-label="Package options"
      aria-orientation="horizontal"
    >
      {entries.map(([key, tier], index) => {
        const selected = key === selectedKey;
        return (
          <button
            key={key}
            id={`package-tier-tab-${key}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls="package-tier-panel"
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(key)}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                event.preventDefault();
                moveSelection(index + 1);
              } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                event.preventDefault();
                moveSelection(index - 1);
              }
            }}
            className={cn(
              "relative inline-flex items-center justify-center rounded border p-3 py-1 text-xs md:text-sm lg:rounded-l lg:px-3 lg:py-2",
              "before:absolute before:top-[calc(100%-3px)] before:left-[calc(50%-8px)] before:rotate-90 before:border-y-8 before:border-l-8 before:border-y-transparent lg:before:top-[calc(50%-8px)] lg:before:left-full lg:before:rotate-0",
              selected
                ? "border-primary bg-primary text-white before:border-l-primary"
                : "border-line bg-white before:border-l-transparent hover:border-primary hover:bg-primary/5 hover:text-primary",
            )}
          >
            {tier.alias || tier.name || key}
          </button>
        );
      })}
    </div>
  );
}
